import { ErrorNotification, useLocalStorage } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { isDefined } from '@utils/general'
import { useEffect } from 'react'

export type UserSettingsHook = ReturnType<typeof useUserSettings>

export const useUserSettings = () => {
  const setupSettings = (data: {}) => {
    let reload = false
    for (const [key, value] of Object.entries(data)) {
      try {
        const parsed = value !== 'undefined' ? JSON.parse(value) : false
        if (typeof parsed === 'string' && key !== 'dispatch-view-settings::trip-time-display') {
          if (key === 'TYPE_OF_THEME') {
            window.localStorage.getItem(`${key}`) !== parsed && (reload = true)
          }

          window.localStorage.setItem(`${key}`, parsed)
        } else {
          window.localStorage.setItem(`${key}`, `${value}`)
        }
      } catch (err) {
        if (key === 'TYPE_OF_THEME' && typeof value === 'string') {
          window.localStorage.getItem(`${key}`) !== value && (reload = true)
          window.localStorage.setItem(`${key}`, `${value}`)
        }
      }
    }
    return reload
  }
  const updateSetting = async (storageKey: string, payload: any) => {
    try {
      const formData = new FormData()
      formData.append('value', JSON.stringify(payload))
      await api.updateUserSetting(storageKey, formData)
    } catch (err) {
      ErrorNotification({
        errorFields: [
          {
            errors: [`Unable to save setting: ${storageKey}`],
          },
        ],
      })
      console.log('no update', { err, storageKey })
    }
  }

  const getSetting = async (storageKey: string) => {
    try {
      const res = await api.getUserSettingByKey(storageKey)
      setupSettings({ [storageKey]: res.data })
      return res?.data !== 'undefined' ? JSON.parse(res.data) : 'undefined'
    } catch (err) {
      ErrorNotification({
        errorFields: [
          {
            errors: [`Unable to save setting: ${storageKey}`],
          },
        ],
      })
    }
  }
  const resetSettings = async () => {
    await api.resetUserSetting()

    const accessToken = window.localStorage.getItem('accessToken')
    const refresh = window.localStorage.getItem('refresh')
    const token = window.localStorage.getItem('token')
    window.localStorage.clear()
    window.localStorage.setItem('accessToken', accessToken)
    window.localStorage.setItem('refresh', refresh)
    window.localStorage.setItem('token', token)
    window.localStorage.setItem('TYPE_OF_THEME', 'LIGHT_MODE')
    window.localStorage.setItem('USE_NETWORK_TIME', 'false')
    window.location.reload()
  }

  return {
    updateSetting,
    getSetting,
    resetSettings,
    setupSettings,
  }
}

export function useSyncedLocalStorage<T>(key: string, defaultValue?: T | (() => T)) {
  const { getSetting, updateSetting } = useUserSettings()
  const { value, setValue: _setValue } = useLocalStorage(key, defaultValue)

  const setValue = (newValue: T) => {
    _setValue(newValue)
    updateSetting(key, newValue)
  }

  // On load, fetch the setting from the server and set the local storage value
  useEffect(() => {
    async function fetchSetting() {
      const setting = await getSetting(key)
      if (isDefined(setting)) {
        setValue(setting)
      }
    }
    fetchSetting()
  }, [])

  return [value, setValue]
}
